import {Link} from '@github-ui/react-router'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {XIcon} from '@primer/octicons-react'
import {Button, Link as PrimerLink, Text} from '@primer/react'
import {useCallback, useState} from 'react'

import type {SidebarSuggestedWorkflowsProps, SuggestedWorkflowItem} from '../types'
import {SidebarSection} from './SidebarSection'
import styles from './SidebarSuggestedWorkflows.module.css'

function WorkflowCard({workflow}: {workflow: SuggestedWorkflowItem}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  return (
    <li className={styles.card}>
      <div className={styles.cardIcon} style={{color: workflow.langColor}}>
        {workflow.iconRawUrl ? (
          <img className={styles.iconImg} src={workflow.iconRawUrl} alt={`${workflow.name} logo`} />
        ) : (
          <img
            className={styles.iconImg}
            src={`/images/icons/actions/${workflow.iconName}`}
            alt={`${workflow.name} logo`}
          />
        )}
      </div>
      <div className={styles.cardBody}>
        <span className={styles.cardName}>{workflow.name}</span>
        {workflow.description && (
          <Text as="span" size="small" className={styles.cardDescription}>
            {workflow.description}
          </Text>
        )}
        <Text as="span" size="small" className={styles.cardCreator}>
          By {workflow.creator}
        </Text>
      </div>
      <Button
        as="a"
        href={workflow.configurePath}
        size="small"
        className={styles.configureButton}
        onClick={() => {
          sendClickAnalyticsEvent({
            category: 'suggested_workflows_in_repository_sidebar',
            action: 'clicked_configure_workflow',
            label: `location:sidebar;section:suggested_workflows;rank:${workflow.rank};template:${workflow.id}`,
          })
        }}
      >
        Configure
      </Button>
    </li>
  )
}

export function SidebarSuggestedWorkflows({
  workflows,
  moreWorkflowsPath,
  dismissPath,
  noticeName,
  repositoryId,
}: SidebarSuggestedWorkflowsProps) {
  const [dismissed, setDismissed] = useState(false)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const handleDismiss = useCallback(async () => {
    setDismissed(true)
    await verifiedFetch(`${dismissPath}?notice_name=${noticeName}&repository_id=${repositoryId}`, {
      method: 'DELETE',
    })
    sendClickAnalyticsEvent({
      category: 'suggested_workflows_in_repository_sidebar',
      action: 'dismissed_component',
      label: 'location:sidebar;section:suggested_workflows',
    })
  }, [dismissPath, noticeName, repositoryId, sendClickAnalyticsEvent])

  if (dismissed) return null

  return (
    <SidebarSection title="Suggested workflows">
      <Text as="p" size="small" className={styles.description}>
        Based on your tech stack
      </Text>
      <ol className={styles.list}>
        {workflows.map(workflow => (
          <WorkflowCard key={workflow.id} workflow={workflow} />
        ))}
      </ol>
      <div className={styles.footer}>
        <PrimerLink
          as={Link}
          to={moreWorkflowsPath}
          className="text-small"
          onClick={() => {
            sendClickAnalyticsEvent({
              category: 'suggested_workflows_in_repository_sidebar',
              action: 'clicked_on_see_more_button',
              label: 'location:sidebar;section:suggested_workflows',
            })
          }}
        >
          More workflows
        </PrimerLink>
        <Button
          variant="invisible"
          size="small"
          aria-label="Dismiss suggestions"
          onClick={handleDismiss}
          className={styles.dismissButton}
        >
          <XIcon size={16} />
        </Button>
      </div>
    </SidebarSection>
  )
}
